"use client";

import { useMemo, useState } from "react";

import { FACET_LABELS, FacetKey, FacetOption } from "@/@types/filters";
import FilterOption from "./FilterOption";
import styles from "./Filters.module.scss";

interface Props {
  facetKey: FacetKey;
  options: FacetOption[];
  isSelected: (key: FacetKey, value: string) => boolean;
  onToggle: (key: FacetKey, value: string) => void;
}

/**
 * Acima deste número de opções o grupo ganha campo de busca e passa a mostrar
 * só uma parte, com "ver mais". Grupos curtos (cor, material) seguem inteiros.
 */
const LIMIAR = 20;

/** Ignora acento e caixa: "ceramica" precisa achar "Cerâmica". */
function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * Um grupo genérico, dirigido por dados. Antes cada grupo era um bloco de JSX
 * copiado, e adicionar um quinto grupo significava copiar de novo.
 */
export default function FilterGroup({ facetKey, options, isSelected, onToggle }: Props) {
  const [expandido, setExpandido] = useState(false);
  const [busca, setBusca] = useState("");

  const termo = normalizar(busca);

  const correspondentes = useMemo(
    () => (termo ? options.filter((o) => normalizar(o.label).includes(termo)) : options),
    [options, termo]
  );

  // Enquanto colapsado, as opções já marcadas continuam visíveis mesmo que
  // caiam fora do corte — sumir com o que o usuário acabou de marcar confunde.
  const visiveis = useMemo(() => {
    const colapsar = !expandido && !termo && correspondentes.length > LIMIAR;
    if (!colapsar) return correspondentes;

    const inicio = correspondentes.slice(0, LIMIAR);
    const marcadasDeFora = correspondentes
      .slice(LIMIAR)
      .filter((o) => isSelected(facetKey, o.value));

    return [...inicio, ...marcadasDeFora];
  }, [correspondentes, expandido, termo, facetKey, isSelected]);

  if (!options.length) return null;

  const temBusca = options.length > LIMIAR;
  const ocultas = correspondentes.length - visiveis.length;
  const titleId = `filtro-grupo-${facetKey}`;
  const buscaId = `filtro-busca-${facetKey}`;

  return (
    <section className={styles.group} role="group" aria-labelledby={titleId}>
      <h4 className={styles.subtitle} id={titleId}>
        {FACET_LABELS[facetKey]}
      </h4>

      {temBusca && (
        <>
          <label className={styles.srOnly} htmlFor={buscaId}>
            Buscar em {FACET_LABELS[facetKey]}
          </label>
          <input
            id={buscaId}
            type="search"
            className={styles.groupSearch}
            placeholder={`Buscar em ${FACET_LABELS[facetKey].toLowerCase()}…`}
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            autoComplete="off"
          />
        </>
      )}

      <div className={styles.options}>
        {visiveis.map((option) => (
          <FilterOption
            key={`${facetKey}-${option.value}`}
            facetKey={facetKey}
            option={option}
            selected={isSelected(facetKey, option.value)}
            onToggle={onToggle}
          />
        ))}
      </div>

      {termo && correspondentes.length === 0 && (
        <p className={styles.groupEmpty}>Nada encontrado para “{busca}”.</p>
      )}

      {ocultas > 0 && (
        <button
          type="button"
          className={styles.showMore}
          onClick={() => setExpandido(true)}
          aria-expanded={false}
        >
          Ver mais {ocultas}
        </button>
      )}

      {expandido && !termo && correspondentes.length > LIMIAR && (
        <button
          type="button"
          className={styles.showMore}
          onClick={() => setExpandido(false)}
          aria-expanded
        >
          Ver menos
        </button>
      )}
    </section>
  );
}
